/**
 * The end of an operation (04 §2.4): the stamped result, the company roll as it finished, and -
 * after a victory that was not a Cadet run - the induction of survivors into the Hall of Honor
 * (one, or two on Hard and Suicide). Every other ending says plainly why nobody is inducted.
 */

import './setup.css';
import type { Operation, RangerRecord } from '../../meta/types';
import type { Ctx, Screen } from './context';
import { box, button, el, errorMessage, panel, show } from './dom';
import { difficultyName, serviceName } from './text';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';
import type { StampSpec } from './widgets/stamp';
import { stamp } from './widgets/stamp';

const STAMPS: Readonly<Record<Operation['status'], StampSpec>> = {
  won: { text: 'OPERATION COMPLETE', tone: 'green' },
  lost: { text: 'OPERATION LOST', tone: 'red' },
  withdrawn: { text: 'WITHDRAWN', tone: 'amber' },
  active: { text: 'OPERATION ACTIVE', tone: 'amber' },
};

/** Survivors are those who could still serve: the dead, the lost and the captive cannot be inducted. */
export function isSurvivor(r: RangerRecord): boolean {
  return r.status !== 'kia' && r.status !== 'lost' && r.status !== 'pow' && r.status !== 'retired';
}

/** Hall places a victory earns (04 §2.4): two on the hardest difficulties. */
export function inductionsAllowed(op: Operation): number {
  if (op.status !== 'won' || op.cadet) return 0;
  return op.difficulty >= 2 ? 2 : 1;
}

function verdict(op: Operation): string {
  if (op.status === 'won') {
    return op.cadet
      ? 'CADET RUN: NOBODY IS INDUCTED. THE RESTARTS WERE THE PRICE.'
      : 'THE THIRD KEYSTONE HAS FALLEN. CHOOSE WHO ENTERS THE HALL OF HONOR.';
  }
  if (op.status === 'lost') {
    return 'THE OPERATION WAS NOT WON: NOBODY IS INDUCTED. THE FALLEN ARE ON THE MEMORIAL WALL.';
  }
  if (op.status === 'withdrawn') return 'WITHDRAWN: SURVIVORS ARE NOT INDUCTED. UNLOCK PROGRESS IS KEPT.';
  return 'THIS OPERATION IS STILL BEING FOUGHT.';
}

export function operationEndScreen(ctx: Ctx): Screen {
  const f = frame('setup', 'OPERATION REPORT');
  const toTitle = button('RETURN TO TITLE', () => ctx.nav.title());
  const op = ctx.api.profile().operation;
  if (op === null) {
    f.body.append(box('menu-col menu-setup-blocked', el('div', 'menu-head-title', 'NO OPERATION ON RECORD'), toTitle));
    return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => toTitle };
  }

  f.info.append(el('span', 'menu-paper', op.codename), el('span', '', difficultyName(op.difficulty)));
  if (op.cadet) f.info.append(el('span', 'menu-chip menu-chip-dim', 'CADET'));

  const allowed = inductionsAllowed(op);
  const inducted = new Set<string>();
  const error = el('div', 'menu-error menu-hidden');
  const places = el('div', 'menu-paper');
  const roll = el('div', 'menu-end-roll');

  let firstInduct: HTMLButtonElement | null = null;

  const render = (): void => {
    // The Hall is the record; the local set only covers an API that re-ids a veteran on induction.
    // Veterans who rode along already belong to the Hall: they take no place and need no button.
    const hall = new Set(ctx.api.profile().hall.map((r) => r.id));
    const isIn = (r: RangerRecord): boolean => !r.veteran && (hall.has(r.id) || inducted.has(r.id));
    const left = Math.max(0, allowed - op.company.filter(isIn).length);
    places.textContent = allowed === 0 ? '' : `HALL OF HONOR PLACES LEFT: ${left} OF ${allowed}`;
    firstInduct = null;
    const cards = op.company.map((r) => {
      const card = dossierCard(r, { describe: ctx.describe, compact: true });
      if (isIn(r)) {
        card.append(stamp({ text: 'INDUCTED', tone: 'green' }, true));
      } else if (left > 0 && !r.veteran && isSurvivor(r)) {
        const b = button('INDUCT', () => induct(r), 'menu-btn-small');
        b.setAttribute('aria-label', `INDUCT ${serviceName(r)} INTO THE HALL OF HONOR`);
        card.append(box('menu-dossier-actions', b));
        firstInduct ??= b;
      }
      return card;
    });
    roll.replaceChildren(...cards);
  };

  const induct = (r: RangerRecord): void => {
    try {
      ctx.api.inductVeteran(r.id);
    } catch (err) {
      error.textContent = `${errorMessage(err)} - RETIRE A VETERAN IN THE HALL OF HONOR, THEN COME BACK`
        + ' THROUGH NEW OPERATION > LAST OPERATION.';
      show(error, true);
      return;
    }
    show(error, false);
    inducted.add(r.id);
    render();
    // The button that was pressed went with the old cards; focus must not fall to the page.
    (firstInduct ?? toTitle).focus();
  };

  const result = panel('RESULT', 'menu-end-left');
  result.body.append(
    box('menu-end-stamp', stamp(STAMPS[op.status])),
    el('div', 'menu-prose menu-paper', verdict(op)),
    places,
    ...op.log.slice(-4).map((line) => el('div', 'menu-prose menu-dim', line)),
  );
  const company = panel('THE COMPANY', 'menu-grow');
  company.body.append(roll);
  render();

  const actions = el('div', 'menu-setup-actions');
  if (op.status === 'active') actions.append(button('TO THE THEATRE MAP', () => ctx.nav.theatre()));
  if (allowed > 0) actions.append(button('HALL OF HONOR', () => ctx.nav.hall()));
  actions.append(toTitle);
  const main = box('menu-setup-main', result.panel, company.panel);
  f.body.append(box('menu-col menu-grow menu-setup', main, error, actions));
  // After a win the induction comes first: Enter must not skip it on the way to the title.
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => firstInduct ?? toTitle };
}
